import cx from 'classnames'
import { Spinner } from 'common/Spinner/Spinner'
import { generatePath, useHistory } from 'react-router-dom'

import { ReactComponent as CheckIcon } from 'assets//icons/check16.svg'
import { ReactComponent as ArrowUpIcon } from 'assets/icons/arrowUp16.svg'
import { ReactComponent as RightArrow } from 'assets/icons/blockyRightArrow16.svg'
import { ReactComponent as DocumentIcon } from 'assets/icons/document24.svg'
import { ReactComponent as NameIcon } from 'common/PropertyItem/icons/text__16.svg'
import { ButtonLink } from 'components/Button/ButtonLink'
import { handbookLink } from 'components/Handbook2_0/util'
import { ReactComponent as CheckCircle } from 'components/ImportModal/icons/checkCircle.svg'
import { ReactComponent as FailedIcon } from 'components/ImportModal/icons/failed.svg'
import { FOLDER_DOC_PATH } from 'paths'
import { AlmanacFile } from 'types'
import { getAllDocsPath, getArticlePath, getRecentDocsPath } from 'utils/urlUtils'
import Styles from './ImportingFiles.module.scss'
import { FileImport, FileImportStatus, ImportStateType } from './Types'
import { Modal } from 'almanac-ui/Modal'

const UPLOAD_STATUS_MSGS = {
  [FileImportStatus.Initiated]: 'Uploading',
  [FileImportStatus.Uploading]: 'Uploading',
  [FileImportStatus.Processing]: 'Uploading',
  [FileImportStatus.PartiallySuccessful]: 'Uploading',
  [FileImportStatus.Successful]: 'Done',
  [FileImportStatus.Cancelled]: 'Cancelled',
  [FileImportStatus.Failed]: 'Failed',
}

const statusIcon = (status: FileImportStatus) => {
  switch (status) {
    case FileImportStatus.Initiated:
    case FileImportStatus.Uploading:
    case FileImportStatus.PartiallySuccessful:
    case FileImportStatus.Processing:
      return <Spinner />
    case FileImportStatus.Successful:
      return (
        <div className={Styles.iconGreen}>
          <CheckIcon />
        </div>
      )
    case FileImportStatus.Failed:
    case FileImportStatus.Cancelled:
      return (
        <div className={Styles.iconRed}>
          <FailedIcon />
        </div>
      )
  }
}

interface ImportingProp {
  fileImports: FileImport[]
  folder: AlmanacFile | null
  workspaceId: number
  importState: ImportStateType
  onClose: () => void
}

export const ImportingFiles = ({ fileImports, onClose, importState, folder, workspaceId }: ImportingProp) => {
  const history = useHistory()

  const openFolder = () => {
    const url = folder?.handbook ? handbookLink(folder.handbook) : getRecentDocsPath(workspaceId)
    history.push(url)
    onClose()
  }

  const DefaultStatus = ({ status }: { status: FileImportStatus }) => {
    return (
      <>
        <div>{statusIcon(status)}</div>
        <div
          className={cx({
            [Styles.textRed]: [FileImportStatus.Cancelled, FileImportStatus.Failed].includes(status),
            [Styles.textGreen]: status === FileImportStatus.Successful,
          })}
        >
          {UPLOAD_STATUS_MSGS[status]}
        </div>
      </>
    )
  }

  const CompleteStateActions = ({ status, openFileUrl }: { status: FileImportStatus; openFileUrl: string }) => {
    if (status === FileImportStatus.Successful)
      return (
        <ButtonLink size="md" variant="tertiaryGreen" endIcon={RightArrow} to={openFileUrl}>
          Open
        </ButtonLink>
      )

    return <DefaultStatus status={status} />
  }

  const openFile = (item: FileImport) => {
    if (item.articles.length === 0) return ''
    if (item.articles.length > 1) {
      return folder?.handbook ? handbookLink(folder.handbook) : getAllDocsPath(workspaceId)
    }

    return !!folder?.handbook?.seo_string
      ? generatePath(FOLDER_DOC_PATH, { folderSlug: folder.handbook.seo_string, doc: item.articles[0].key })
      : getArticlePath({ slug: item.articles[0].key })
  }

  return (
    <>
      <Modal.Content className={Styles.importingFilesContents}>
        <div className={Styles.tableHeader}>
          <div className={Styles.iconContainer}>
            <NameIcon />
            <div>Name</div>
          </div>
          <div className={Styles.iconContainerSlim}>
            <span className={Styles.iconGrey}>
              <CheckCircle />
            </span>
            <div className={Styles.marginLeft4}>Status</div>
            <span className={Styles.iconGrey}>
              <ArrowUpIcon />
            </span>
          </div>
        </div>
        {fileImports.map((item, index) => {
          return (
            <div className={Styles.importingRow} key={`${item.originalFile.name}-${index}`}>
              <div className={Styles.iconContainer}>
                <span className={Styles.iconGrey}>
                  <DocumentIcon />
                </span>
                <span>{item.originalFile.name}</span>
              </div>
              <div className={Styles.iconContainer}>
                {importState === ImportStateType.Importing ? (
                  <DefaultStatus status={item.status} />
                ) : (
                  <CompleteStateActions status={item.status} openFileUrl={openFile(item)} />
                )}
              </div>
            </div>
          )
        })}
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="right">
          {importState === ImportStateType.Importing ? (
            <Modal.FooterButton onPress={onClose} variant="secondary" size="lg">
              Cancel Import
            </Modal.FooterButton>
          ) : (
            <Modal.FooterButton onPress={openFolder} size="lg">
              Open {folder?.title || 'Imported'}
            </Modal.FooterButton>
          )}
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
